import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText, Calendar, User, Building2, Clock,
  CheckCircle, XCircle, Send, AlertCircle, Paperclip,
  MessageSquare, BarChart3, Download, Stamp, ArrowRightLeft,
  Info, Play, Eye, MessageCircle
} from 'lucide-react';
import api, { downloadBlob } from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './ReportDetail.css';

// CSS-классы статусов (метки берутся из i18n)
const STATUS_CSS = {
  draft:     'status-draft',
  submitted: 'status-submitted',
  in_review: 'status-review',
  approved:  'status-approved',
  rejected:  'status-rejected',
  archived:  'status-archived',
};

// Цвета и иконки резолюций (метки берутся из i18n)
const RESOLUTION_TYPE_COLORS = {
  approve:  { color: '#22c55e', icon: CheckCircle },
  reject:   { color: '#ef4444', icon: XCircle },
  rework:   { color: '#f59e0b', icon: AlertCircle },
  delegate: { color: '#3b82f6', icon: ArrowRightLeft },
  info:     { color: '#64748b', icon: Info },
  execute:  { color: '#8b5cf6', icon: Play },
};

function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['reports', 'common']);

  // Функция для получения конфига резолюции с переведённой меткой
  const getResolutionConfig = (type) => {
    const colors = RESOLUTION_TYPE_COLORS[type] || RESOLUTION_TYPE_COLORS.info;
    return { ...colors, label: t(`reports:resolution.types.${type}`, type) };
  };

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resolutions, setResolutions] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Модал резолюции
  const [showResModal, setShowResModal] = useState(false);
  const [resType, setResType] = useState('');
  const [resText, setResText] = useState('');
  const [resDelegatedTo, setResDelegatedTo] = useState('');
  const [resDeadline, setResDeadline] = useState('');
  const [resSubmitting, setResSubmitting] = useState(false);
  const [deptTemplate, setDeptTemplate] = useState(null);

  // Комментарии к полям отчёта
  const [fieldCommentSummary, setFieldCommentSummary] = useState({});
  const [showFieldCommentModal, setShowFieldCommentModal] = useState(false);
  const [activeFieldKey, setActiveFieldKey] = useState(null);
  const [activeFieldLabel, setActiveFieldLabel] = useState('');
  const [fieldComments, setFieldComments] = useState([]);
  const [fieldCommentText, setFieldCommentText] = useState('');
  const [fieldCommentInternal, setFieldCommentInternal] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [fieldCommentsLoading, setFieldCommentsLoading] = useState(false);
  const [fieldCommentSubmitting, setFieldCommentSubmitting] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getReport(id);
      setReport(response.data);

      // Загрузить шаблон отдела для отображения меток полей
      if (response.data.department) {
        const deptId = response.data.department_id || response.data.department;
        try {
          const tplResp = await api.getTemplateByDepartment(deptId);
          const tplList = tplResp.data.results || tplResp.data || [];
          if (tplList.length > 0) {
            setDeptTemplate(tplList[0]);
          }
        } catch (e) {
          // Шаблон не найден — не критично
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Ошибка загрузки отчёта:', err);
      setError(t('reports:messages.loadError'));
      setLoading(false);
    }
  }, [id]);

  const fetchResolutions = useCallback(async () => {
    try {
      const res = await api.getResolutions({ content_type: 'report', object_id: id });
      setResolutions(res.data?.results || res.data || []);
    } catch (err) {
      console.error('Ошибка загрузки резолюций:', err);
    }
  }, [id]);

  const fetchFieldCommentSummary = useCallback(async () => {
    try {
      const res = await api.getFieldCommentSummary(id);
      setFieldCommentSummary(res.data || {});
    } catch (err) {
      console.error('Ошибка загрузки сводки комментариев:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchReport();
    fetchResolutions();
    fetchFieldCommentSummary();
    api.getDepartments().then(res => {
      setDepartments(res.data?.results || res.data || []);
    }).catch(() => {});
  }, [fetchReport, fetchResolutions, fetchFieldCommentSummary]);

  const handleSubmit = async () => {
    if (window.confirm(t('reports:actions.confirmSubmit'))) {
      try {
        await api.submitReport(id);
        fetchReport();
      } catch (err) {
        alert(t('common:errors.generic') + ': ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleApprove = async () => {
    try {
      await api.approveReport(id);
      fetchReport();
    } catch (err) {
      alert(t('reports:messages.approveError') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleReject = async () => {
    const reason = prompt(t('reports:actions.enterRejectionReason'));
    if (reason) {
      try {
        await api.rejectReport(id, reason);
        fetchReport();
      } catch (err) {
        alert(t('reports:messages.rejectError') + ': ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleCreateResolution = async () => {
    if (!resType || !resText.trim()) return;
    try {
      setResSubmitting(true);
      const data = {
        content_type_name: 'report',
        object_id: parseInt(id),
        resolution_type: resType,
        text: resText,
      };
      if (resDelegatedTo) data.delegated_to = parseInt(resDelegatedTo);
      if (resDeadline) data.deadline = resDeadline;

      await api.createResolution(data);

      setShowResModal(false);
      setResType('');
      setResText('');
      setResDelegatedTo('');
      setResDeadline('');
      fetchResolutions();
    } catch (err) {
      alert(t('common:errors.generic') + ': ' + (err.response?.data?.detail || JSON.stringify(err.response?.data) || err.message));
    } finally {
      setResSubmitting(false);
    }
  };

  const handleExecuteResolution = async (resId) => {
    try {
      await api.executeResolution(resId);
      fetchResolutions();
    } catch (err) {
      alert(t('common:errors.generic') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  // ── Комментарии к полям ──

  const openFieldComments = async (fieldKey, fieldLabel) => {
    setActiveFieldKey(fieldKey);
    setActiveFieldLabel(fieldLabel);
    setShowFieldCommentModal(true);
    setReplyTo(null);
    setFieldCommentText('');
    setFieldCommentInternal(false);
    setFieldCommentsLoading(true);
    try {
      const res = await api.getFieldComments(id, fieldKey);
      setFieldComments(res.data?.results || res.data || []);
    } catch (err) {
      console.error('Ошибка загрузки комментариев поля:', err);
    } finally {
      setFieldCommentsLoading(false);
    }
  };

  const handleFieldCommentSubmit = async () => {
    if (!fieldCommentText.trim()) return;
    try {
      setFieldCommentSubmitting(true);
      const data = {
        report: parseInt(id),
        text: fieldCommentText,
        field_key: activeFieldKey,
        is_internal: fieldCommentInternal,
      };
      if (replyTo) data.parent = replyTo;
      await api.createReportComment(data);
      setFieldCommentText('');
      setReplyTo(null);
      setFieldCommentInternal(false);
      const res = await api.getFieldComments(id, activeFieldKey);
      setFieldComments(res.data?.results || res.data || []);
      fetchFieldCommentSummary();
      fetchReport();
    } catch (err) {
      alert(t('common:errors.generic') + ': ' + (err.response?.data?.detail || JSON.stringify(err.response?.data) || err.message));
    } finally {
      setFieldCommentSubmitting(false);
    }
  };

  const handleResolveComment = async (commentId) => {
    try {
      await api.resolveReportComment(commentId);
      const res = await api.getFieldComments(id, activeFieldKey);
      setFieldComments(res.data?.results || res.data || []);
      fetchFieldCommentSummary();
      fetchReport();
    } catch (err) {
      alert(t('common:errors.generic') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  const getStatusBadge = (s) => {
    const cssClass = STATUS_CSS[s] || 'status-draft';
    const label = t(`common:reportStatuses.${s}`, s);
    return <span className={`status-badge ${cssClass}`}>{label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(getDateLocale(), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(getDateLocale(), {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="report-detail">
        <div className="loading-spinner">{t('reports:loadingReport')}</div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="report-detail">
        <div className="error-state">
          <AlertCircle size={48} />
          <h3>{error || t('reports:messages.notFound')}</h3>
          <button className="btn-back" onClick={() => navigate(-1)}>
            {t('common:buttons.back')}
          </button>
        </div>
      </div>
    );
  }

  // RBAC: утверждение — руководство (все) + department_head (свой отдел)
  const leadershipRoles = ['admin', 'director', 'deputy_director'];
  const isUserLeadership = user.is_staff || user.is_superuser || leadershipRoles.includes(user.role);
  const canApproveUser = isUserLeadership || (
    user.role === 'department_head' &&
    (user.department_id || user.department) === report.department
  );
  const canSubmit = report.status === 'draft';
  const canApprove = canApproveUser && (report.status === 'submitted' || report.status === 'in_review');
  const canResolve = isUserLeadership || user.role === 'department_head';

  return (
    <div className="report-detail">
      {/* Шапка */}
      <div className="detail-header">
        <div className="detail-header-actions">
          {canSubmit && (
            <button className="btn-action btn-submit" onClick={handleSubmit}>
              <Send size={16} /> {t('reports:actions.submitForReview')}
            </button>
          )}
          {canApprove && (
            <>
              <button className="btn-action btn-approve" onClick={handleApprove}>
                <CheckCircle size={16} /> {t('reports:actions.approveReport')}
              </button>
              <button className="btn-action btn-reject" onClick={handleReject}>
                <XCircle size={16} /> {t('reports:actions.rejectReport')}
              </button>
            </>
          )}
          {canResolve && (
            <button className="btn-action btn-resolution" onClick={() => setShowResModal(true)}>
              <Stamp size={16} /> {t('reports:actions.addResolution')}
            </button>
          )}
          <button className="btn-export" onClick={async () => {
            try { const r = await api.exportReportWord({ report_id: report.id }); downloadBlob(r, `Отчёт_${report.id}.docx`); } catch {}
          }}><Download size={16} /> {t('reports:actions.exportWord')}</button>
        </div>
      </div>

      {/* Заголовок отчёта */}
      <div className="detail-title-card">
        <div className="detail-title-row">
          <h1>{report.title}</h1>
          {getStatusBadge(report.status)}
        </div>

        <div className="detail-meta">
          <div className="meta-item">
            <Building2 size={16} />
            <span>{report.department_name}</span>
          </div>
          <div className="meta-item">
            <Calendar size={16} />
            <span>{report.period_name}</span>
          </div>
          <div className="meta-item">
            <User size={16} />
            <span>{report.author_name}</span>
          </div>
          <div className="meta-item">
            <Clock size={16} />
            <span>{formatDate(report.created_at)}</span>
          </div>
        </div>

        {report.submitted_at && (
          <div className="meta-item secondary">
            <Send size={14} /> {t('reports:timing.submittedAt')}: {formatDate(report.submitted_at)}
            {report.is_on_time === true && (
              <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#16a34a' }}>
                {t('reports:timing.onTime')}
              </span>
            )}
            {report.is_on_time === false && (
              <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fef2f2', color: '#dc2626' }}>
                {t('reports:timing.delayHours', { hours: report.delay_hours || 0 })}
              </span>
            )}
          </div>
        )}
        {report.approved_at && (
          <div className="meta-item secondary">
            <CheckCircle size={14} /> {t('reports:timing.approvedAt')}: {formatDate(report.approved_at)}
            {report.approved_by_name && ` (${report.approved_by_name})`}
          </div>
        )}
      </div>

      {/* Причина отклонения */}
      {report.rejection_reason && (
        <div className="rejection-card">
          <AlertCircle size={20} />
          <div>
            <strong>{t('reports:sections.rejectionReason')}:</strong>
            <p>{report.rejection_reason}</p>
          </div>
        </div>
      )}

      {/* Резолюции */}
      {resolutions.length > 0 && (
        <div className="detail-section resolutions-section">
          <h2><Stamp size={18} /> {t('reports:sections.resolutions')} ({resolutions.length})</h2>
          <div className="resolutions-list">
            {resolutions.map(res => {
              const cfg = getResolutionConfig(res.resolution_type);
              const Icon = cfg.icon;
              return (
                <div key={res.id} className={`resolution-card ${res.is_executed ? 'executed' : ''}`}>
                  <div className="resolution-card-header">
                    <span className="resolution-type-badge" style={{ background: cfg.color }}>
                      <Icon size={14} /> {cfg.label}
                    </span>
                    <span className="resolution-author">{res.author_name}</span>
                    {res.author_position && (
                      <span className="resolution-position">{res.author_position}</span>
                    )}
                    <span className="resolution-date">{formatDate(res.created_at)}</span>
                  </div>
                  <p className="resolution-text">{res.text}</p>
                  <div className="resolution-meta">
                    {res.delegated_to_name && (
                      <span className="resolution-delegate">
                        <Building2 size={13} /> {res.delegated_to_name}
                      </span>
                    )}
                    {res.deadline && (
                      <span className="resolution-deadline">
                        <Calendar size={13} /> {t('reports:resolution.executionDeadline')}: {formatShortDate(res.deadline)}
                      </span>
                    )}
                    {res.is_executed ? (
                      <span className="resolution-executed">
                        <CheckCircle size={13} /> {t('reports:resolution.executed')} {res.executed_at ? formatShortDate(res.executed_at) : ''}
                      </span>
                    ) : (
                      (res.resolution_type === 'execute' || res.resolution_type === 'delegate' || res.resolution_type === 'rework') && (
                        <button
                          className="btn-small btn-execute-res"
                          onClick={() => handleExecuteResolution(res.id)}
                        >
                          <Eye size={14} /> {t('reports:resolution.executed')}
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Секции контента */}
      <div className="detail-sections">
        {report.summary && (
          <div className="detail-section">
            <h2><FileText size={18} /> {t('reports:sections.summary')}</h2>
            <p>{report.summary}</p>
          </div>
        )}

        {report.main_work && (
          <div className="detail-section">
            <h2><FileText size={18} /> {t('reports:sections.mainWork')}</h2>
            <p>{report.main_work}</p>
          </div>
        )}

        {report.achievements && (
          <div className="detail-section">
            <h2><CheckCircle size={18} /> {t('reports:sections.achievements')}</h2>
            <p>{report.achievements}</p>
          </div>
        )}

        {report.problems && (
          <div className="detail-section">
            <h2><AlertCircle size={18} /> {t('reports:sections.problems')}</h2>
            <p>{report.problems}</p>
          </div>
        )}

        {report.plans_next_period && (
          <div className="detail-section">
            <h2><Calendar size={18} /> {t('reports:sections.plansNextPeriod')}</h2>
            <p>{report.plans_next_period}</p>
          </div>
        )}
      </div>

      {/* Метрики */}
      {report.metrics && report.metrics.length > 0 && (
        <div className="detail-section">
          <h2><BarChart3 size={18} /> {t('reports:sections.metrics')}</h2>
          <div className="metrics-table">
            <table>
              <thead>
                <tr>
                  <th>{t('reports:metrics.indicator')}</th>
                  <th>{t('reports:metrics.value')}</th>
                  <th>{t('reports:metrics.unit')}</th>
                  <th>{t('reports:metrics.description')}</th>
                </tr>
              </thead>
              <tbody>
                {report.metrics.map(metric => (
                  <tr key={metric.id}>
                    <td>{metric.metric_name}</td>
                    <td className="metric-value">{metric.metric_value}</td>
                    <td>{metric.unit || '—'}</td>
                    <td>{metric.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Данные отдела из шаблона */}
      {report.department_data && report.department_data.data && Object.keys(report.department_data.data).length > 0 && (
        <div className="detail-section">
          <h2><BarChart3 size={18} /> {t('reports:sections.departmentTemplate')}</h2>
          <div className="dept-data-grid">
            {(() => {
              const data = report.department_data.data;
              const fields = deptTemplate?.fields_structure || [];
              // Построить маппинг key → label из шаблона
              const labelMap = {};
              fields.forEach(f => { labelMap[f.key] = f.label; });

              return Object.entries(data).map(([key, value]) => {
                const summary = fieldCommentSummary[key];
                const hasUnresolved = summary && summary.unresolved > 0;
                const hasComments = summary && summary.total > 0;
                return (
                  <div className="dept-data-item" key={key}>
                    <div className="dept-data-header">
                      <span className="dept-data-label">{labelMap[key] || key}</span>
                      <button
                        className={`field-comment-btn ${hasUnresolved ? 'has-unresolved' : ''} ${hasComments ? 'has-comments' : ''}`}
                        onClick={() => openFieldComments(key, labelMap[key] || key)}
                        title={hasComments ? `${t('reports:sections.comments')}: ${summary.total} (${t('reports:fieldCommentModal.unresolvedCount', { count: summary.unresolved })})` : t('reports:fieldComments.addComment')}
                      >
                        <MessageCircle size={14} />
                        {hasComments && <span className="field-comment-count">{summary.total}</span>}
                      </button>
                    </div>
                    <span className="dept-data-value">
                      {value !== null && value !== '' ? String(value) : '—'}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Файлы */}
      {report.files && report.files.length > 0 && (
        <div className="detail-section">
          <h2><Paperclip size={18} /> {t('reports:sections.files')}</h2>
          <div className="files-list">
            {report.files.map(file => (
              <a
                key={file.id}
                href={file.file_url || file.file}
                target="_blank"
                rel="noopener noreferrer"
                className="file-item"
              >
                <Paperclip size={16} />
                <span className="file-name">{file.file_name}</span>
                {file.file_size && (
                  <span className="file-size">
                    {(file.file_size / 1024).toFixed(0)} КБ
                  </span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Сводка замечаний к полям */}
      {Object.keys(fieldCommentSummary).length > 0 && (
        <div className="detail-section">
          <h2><MessageCircle size={18} /> {t('reports:sections.fieldComments')}</h2>
          <div className="field-comments-summary">
            {Object.entries(fieldCommentSummary).map(([key, summary]) => {
              const fields = deptTemplate?.fields_structure || [];
              const labelMap = {};
              fields.forEach(f => { labelMap[f.key] = f.label; });
              return (
                <div
                  key={key}
                  className={`field-summary-item ${summary.unresolved > 0 ? 'has-unresolved' : 'all-resolved'}`}
                  onClick={() => openFieldComments(key, labelMap[key] || key)}
                >
                  <span className="field-summary-label">{labelMap[key] || key}</span>
                  <span className="field-summary-counts">
                    {summary.unresolved > 0 ? (
                      <span className="unresolved-count">{t('reports:fieldCommentModal.unresolvedCount', { count: summary.unresolved })}</span>
                    ) : (
                      <span className="resolved-all">{t('reports:fieldCommentModal.allResolved')}</span>
                    )}
                    <span className="total-count">{t('reports:fieldCommentModal.totalCount', { count: summary.total })}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Общие комментарии (без привязки к полю) */}
      {report.comments && report.comments.filter(c => !c.field_key).length > 0 && (
        <div className="detail-section">
          <h2><MessageSquare size={18} /> {t('reports:sections.comments')}</h2>
          <div className="comments-list">
            {report.comments.filter(c => !c.field_key).map(comment => (
              <div key={comment.id} className={`comment-item ${comment.is_internal ? 'internal' : ''} ${comment.is_resolved ? 'resolved' : ''}`}>
                <div className="comment-header">
                  <strong>{comment.author_name}</strong>
                  {comment.author_position && (
                    <span className="comment-position">{comment.author_position}</span>
                  )}
                  <span className="comment-date">{formatDate(comment.created_at)}</span>
                  {comment.is_internal && (
                    <span className="comment-internal-badge">{t('reports:fieldComments.internal')}</span>
                  )}
                  {comment.is_resolved && (
                    <span className="field-comment-resolved-badge">
                      <CheckCircle size={12} /> {t('reports:fieldComments.resolved')}
                    </span>
                  )}
                </div>
                <p>{comment.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Модал резолюции */}
      {showResModal && (
        <div className="modal-overlay" onClick={() => setShowResModal(false)}>
          <div className="modal-content modal-resolution" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Stamp size={20} /> {t('reports:resolution.addResolution')}</h2>
              <button className="modal-close" onClick={() => setShowResModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('reports:resolution.resolutionType')}</label>
                <div className="resolution-type-grid">
                  {Object.entries(RESOLUTION_TYPE_COLORS).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    const label = t(`reports:resolution.types.${key}`, key);
                    return (
                      <button
                        key={key}
                        className={`res-type-btn ${resType === key ? 'active' : ''}`}
                        style={resType === key ? { borderColor: cfg.color, background: cfg.color + '15' } : {}}
                        onClick={() => setResType(key)}
                      >
                        <Icon size={16} style={{ color: cfg.color }} />
                        <span>{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label>{t('reports:resolution.resolutionText')}</label>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder={t('reports:resolution.resolutionTextPlaceholder')}
                  value={resText}
                  onChange={e => setResText(e.target.value)}
                />
              </div>

              {(resType === 'delegate' || resType === 'execute') && (
                <div className="form-group">
                  <label>{t('reports:resolution.delegateTo')}</label>
                  <select
                    className="form-control"
                    value={resDelegatedTo}
                    onChange={e => setResDelegatedTo(e.target.value)}
                  >
                    <option value="">{t('reports:resolution.selectDepartment')}</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>{t('reports:resolution.executionDeadline')}</label>
                <input
                  type="date"
                  className="form-control"
                  value={resDeadline}
                  onChange={e => setResDeadline(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowResModal(false)}>{t('common:buttons.cancel')}</button>
              <button
                className="btn-primary"
                onClick={handleCreateResolution}
                disabled={!resType || !resText.trim() || resSubmitting}
              >
                {resSubmitting ? t('reports:resolution.saving') : t('reports:resolution.addResolution')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модал комментариев к полю */}
      {showFieldCommentModal && (
        <div className="modal-overlay" onClick={() => setShowFieldCommentModal(false)}>
          <div className="modal-content modal-field-comments" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><MessageCircle size={20} /> {t('reports:fieldCommentModal.title', { field: activeFieldLabel })}</h2>
              <button className="modal-close" onClick={() => setShowFieldCommentModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {fieldCommentsLoading ? (
                <div className="loading-spinner" style={{ minHeight: '100px' }}>{t('common:loading')}</div>
              ) : fieldComments.length === 0 ? (
                <p style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem 0' }}>
                  {t('reports:fieldCommentModal.noComments')}
                </p>
              ) : (
                <div className="field-comments-list">
                  {fieldComments.map(comment => (
                    <div key={comment.id} className={`field-comment-item ${comment.is_resolved ? 'resolved' : ''} ${comment.is_internal ? 'internal' : ''}`}>
                      <div className="field-comment-header">
                        <strong>{comment.author_name}</strong>
                        {comment.author_position && (
                          <span className="field-comment-position">{comment.author_position}</span>
                        )}
                        <span className="field-comment-date">{formatDate(comment.created_at)}</span>
                        {comment.is_internal && <span className="comment-internal-badge">{t('reports:fieldCommentModal.internal')}</span>}
                        {comment.is_resolved && (
                          <span className="field-comment-resolved-badge">
                            <CheckCircle size={12} /> {t('reports:fieldCommentModal.resolved')}
                          </span>
                        )}
                      </div>
                      <p className="field-comment-text">{comment.text}</p>
                      <div className="field-comment-actions">
                        {!comment.is_resolved && (
                          <button className="btn-small" onClick={() => handleResolveComment(comment.id)}>
                            <CheckCircle size={12} /> {t('reports:fieldCommentModal.resolved')}
                          </button>
                        )}
                        <button className="btn-small" onClick={() => setReplyTo(comment.id)}>
                          <MessageSquare size={12} /> {t('reports:fieldCommentModal.reply')}
                        </button>
                      </div>

                      {/* Ответы */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="field-comment-replies">
                          {comment.replies.map(reply => (
                            <div key={reply.id} className={`field-comment-reply ${reply.is_resolved ? 'resolved' : ''}`}>
                              <div className="field-comment-header">
                                <strong>{reply.author_name}</strong>
                                <span className="field-comment-date">{formatDate(reply.created_at)}</span>
                                {reply.is_resolved && (
                                  <span className="field-comment-resolved-badge">
                                    <CheckCircle size={12} /> {t('reports:fieldCommentModal.resolved')}
                                  </span>
                                )}
                              </div>
                              <p className="field-comment-text">{reply.text}</p>
                              {!reply.is_resolved && (
                                <button className="btn-small" onClick={() => handleResolveComment(reply.id)}>
                                  <CheckCircle size={12} /> {t('reports:fieldCommentModal.resolved')}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Форма ввода */}
              <div className="field-comment-input">
                {replyTo && (
                  <div className="reply-indicator">
                    {t('reports:fieldCommentModal.replyTo', { id: replyTo })}
                    <button onClick={() => setReplyTo(null)}>×</button>
                  </div>
                )}
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={t('reports:fieldCommentModal.placeholder')}
                  value={fieldCommentText}
                  onChange={e => setFieldCommentText(e.target.value)}
                />
                <div className="field-comment-input-actions">
                  {isUserLeadership && (
                    <label className="internal-checkbox">
                      <input
                        type="checkbox"
                        checked={fieldCommentInternal}
                        onChange={e => setFieldCommentInternal(e.target.checked)}
                      />
                      {t('reports:fieldCommentModal.internal')}
                    </label>
                  )}
                  <button
                    className="btn-primary"
                    onClick={handleFieldCommentSubmit}
                    disabled={!fieldCommentText.trim() || fieldCommentSubmitting}
                  >
                    {fieldCommentSubmitting ? t('reports:fieldComments.sending') : t('common:buttons.send')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportDetail;
